/**
 * 集中式 API 客户端
 * 管理 JWT token 和所有后端请求
 */

// 生产环境使用完整 URL，本地开发用 Vite 代理
const API_BASE = import.meta.env.PROD
  ? 'https://school-portal-api.oleyyuhello.workers.dev/api'
  : '/api';

// --- 类型定义 ---

export interface User {
  id: number;
  email: string;
  name: string;
  role: 'superadmin' | 'admin' | 'teacher' | 'student' | 'parent';
  created_at?: string;
}

export interface InviteCode {
  id: number;
  code: string;
  teacher_id: number;
  max_uses: number;
  used_count: number;
  is_active: number;
  created_at: string;
  expires_at: string | null;
}

export interface Homework {
  id: number;
  class_id: number | null;
  teacher_id: number;
  student_id: number | null;
  title: string;
  description: string | null;
  file_url: string | null;
  file_name: string | null;
  due_date: string | null;
  created_at: string;
  teacher_name?: string;
  student_name?: string;
}

export interface Feedback {
  id: number;
  student_id: number;
  class_id: number | null;
  teacher_id: number;
  content: string;
  feedback_type: string;
  calendar_event_id: number | null;
  created_at: string;
  student_name?: string;
  teacher_name?: string;
  event_title?: string;
  event_date?: string;
  event_start_time?: string;
  event_end_time?: string;
}

export interface Attendance {
  id: number;
  student_id: number;
  class_id: number | null;
  teacher_id: number;
  date: string;
  status: string;
  note: string | null;
  created_at: string;
  student_name?: string;
}

// --- HTTP 请求核心 ---

interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method: options.method || 'GET',
    headers,
    body:
      options.body instanceof FormData
        ? options.body
        : options.body
          ? JSON.stringify(options.body)
          : undefined,
  });

  if (response.status === 401) {
    // 尝试读取服务端真正的错误信息
    let serverMsg = '登录已过期，请重新登录';
    try {
      const body = await response.json();
      if (body.error) serverMsg = body.error;
    } catch { /* ignore */ }

    // 只清除本地存储，不硬跳转。
    // 派发事件让 AuthProvider 同步清理 React state，避免死循环。
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new Event('auth:expired'));
    throw new ApiError(401, serverMsg);
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: '请求失败' }));
    throw new ApiError(response.status, data.error || `请求失败 (${response.status})`);
  }

  // 对于文件下载等非 JSON 响应
  const contentType = response.headers.get('content-type');
  if (contentType && !contentType.includes('application/json')) {
    return response as unknown as T;
  }

  return response.json();
}

// --- API 模块 ---

export const auth = {
  login: (email: string, password: string) =>
    request<{ token: string; user: User }>('/auth/login', {
      method: 'POST',
      body: { email, password },
    }),

  register: (data: {
    email: string;
    password: string;
    name: string;
    role: string;
    invite_code?: string;
  }) =>
    request<{ token: string; user: User }>('/auth/register', {
      method: 'POST',
      body: data,
    }),
};

export const users = {
  me: () => request<User>('/users/me'),
  getStudents: () => request<User[]>('/users/students'),
  myStudents: () => request<User[]>('/users/my-students'),
  linkStudent: (studentEmail: string) =>
    request<{ message: string; student_name: string }>('/users/link-student', {
      method: 'POST',
      body: { student_email: studentEmail },
    }),
};

export const inviteCodes = {
  create: (maxUses?: number, expiresInDays?: number) =>
    request<InviteCode>('/invite-codes', {
      method: 'POST',
      body: { max_uses: maxUses ?? 10, expires_in_days: expiresInDays ?? 30 },
    }),
  list: () => request<InviteCode[]>('/invite-codes'),
};

export const homework = {
  list: () => request<Homework[]>('/homework'),
  get: (id: number) => request<Homework>(`/homework/${id}`),
  create: (data: FormData) =>
    request<Homework>('/homework', { method: 'POST', body: data }),
  fileUrl: (key: string) => `${API_BASE}/files/${encodeURIComponent(key)}`,
};

export const feedback = {
  list: (studentId?: number) => {
    const params = new URLSearchParams();
    if (studentId) params.set('student_id', String(studentId));
    const qs = params.toString();
    return request<Feedback[]>(`/feedback${qs ? `?${qs}` : ''}`);
  },
  create: (data: {
    student_id: number;
    class_id?: number;
    content: string;
    feedback_type: string;
    calendar_event_id?: number | null;
  }) => request<Feedback>('/feedback', { method: 'POST', body: data }),
  update: (id: number, data: { content?: string; feedback_type?: string; calendar_event_id?: number | null }) =>
    request<Feedback>(`/feedback/${id}`, { method: 'PUT', body: data }),
  delete: (id: number) =>
    request<{ message: string }>(`/feedback/${id}`, { method: 'DELETE' }),
};

// --- 共享日历 ---

export interface CalendarEvent {
  id: number;
  teacher_id: number;
  student_id: number | null;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  created_at: string;
  teacher_name?: string;
  student_name?: string;
}

export const calendar = {
  list: (params?: { teacher_id?: number; student_id?: number; date_from?: string; date_to?: string }) => {
    const qs = new URLSearchParams();
    if (params?.teacher_id) qs.set('teacher_id', String(params.teacher_id));
    if (params?.student_id) qs.set('student_id', String(params.student_id));
    if (params?.date_from) qs.set('date_from', params.date_from);
    if (params?.date_to) qs.set('date_to', params.date_to);
    const query = qs.toString();
    return request<CalendarEvent[]>(`/calendar${query ? `?${query}` : ''}`);
  },
  create: (data: { title: string; date: string; start_time: string; end_time: string; student_id?: number | null }) =>
    request<CalendarEvent>('/calendar', { method: 'POST', body: data }),
  update: (id: number, data: { title?: string; date?: string; start_time?: string; end_time?: string; student_id?: number | null }) =>
    request<CalendarEvent>(`/calendar/${id}`, { method: 'PUT', body: data }),
  delete: (id: number) =>
    request<{ message: string }>(`/calendar/${id}`, { method: 'DELETE' }),
};

export const attendance = {
  list: (date?: string) => {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    const qs = params.toString();
    return request<Attendance[]>(`/attendance${qs ? `?${qs}` : ''}`);
  },
  record: (
    records: {
      student_id: number;
      class_id?: number;
      date: string;
      status: string;
      note?: string;
    }[]
  ) =>
    request<{ records: Attendance[]; count: number }>('/attendance', {
      method: 'POST',
      body: { records },
    }),
};

// --- Admin / Superadmin ---
export const admin = {
  createTeacher: (data: { email: string; password: string; name: string }) =>
    request<{ message: string; user: User }>('/admin/create-teacher', {
      method: 'POST',
      body: data,
    }),
  createStudent: (data: { email: string; password: string; name: string }) =>
    request<{ message: string; user: User }>('/admin/create-student', {
      method: 'POST',
      body: data,
    }),
  createSuperAdmin: (data: { email: string; password: string; name: string }) =>
    request<{ message: string; user: User }>('/admin/create-superadmin', {
      method: 'POST',
      body: data,
    }),
  listUsers: () =>
    request<User[]>('/admin/users'),
  listStudents: () =>
    request<User[]>('/admin/students'),
  deleteUser: (userId: number) =>
    request<{ message: string }>(`/admin/users/${userId}`, {
      method: 'DELETE',
    }),
  updateHomework: (
    id: number,
    data: { title?: string; description?: string; due_date?: string; student_id?: number | null }
  ) =>
    request<Homework>(`/homework/${id}`, {
      method: 'PUT',
      body: data,
    }),
  deleteHomework: (id: number) =>
    request<{ message: string }>(`/homework/${id}`, {
      method: 'DELETE',
    }),
};

// --- 作业提交 ---

export interface Submission {
  id: number;
  homework_id: number;
  student_id: number;
  content: string | null;
  file_url: string | null;
  file_name: string | null;
  created_at: string;
  updated_at: string;
  student_name?: string;
  student_email?: string;
}

export const submissions = {
  /** 学生提交作业 (multipart: homework_id, content?, file?) */
  create: (data: FormData) =>
    request<Submission>('/submissions', { method: 'POST', body: data }),

  /** 获取某作业的所有提交 (老师看全部，学生只看自己的) */
  list: (homeworkId: number) =>
    request<Submission[]>(`/submissions?homework_id=${homeworkId}`),

  /** 学生获取自己某作业的提交 */
  mine: (homeworkId: number) =>
    request<Submission | null>(`/submissions/mine?homework_id=${homeworkId}`),

  /** 获取单个提交 */
  get: (id: number) => request<Submission>(`/submissions/${id}`),

  /** 更新提交 (multipart: content?, file?) */
  update: (id: number, data: FormData) =>
    request<Submission>(`/submissions/${id}`, { method: 'PUT', body: data }),

  /** 删除提交 */
  delete: (id: number) =>
    request<{ message: string }>(`/submissions/${id}`, { method: 'DELETE' }),

  /** 下载提交附件 */
  fileUrl: (key: string) => `${API_BASE}/submissions/files/${encodeURIComponent(key)}`,
};

export { ApiError };
